<template>
  <f7-popup
    v-model:opened="opened"
    class="parts-details-change-date-popup"
  >
    <f7-page v-if="opened">
      <template #fixed>
        <TheNavbar
          class="at-navbar secondary-navbar"
          popup-close-btn-enable
          nav-title="Cambiar fecha y hora"
        />
      </template>

      <f7-block>
        <UCDatePicker
          ref="datePickerRef"
          title="Fecha del parte"
          :default-value="selectedStartDate ? new Date(selectedStartDate) : undefined"
          :disabled="isUpdatingDate"
          @onchange="handleDateChange"
        />

        <UCTimePicker
          ref="timePickerRef"
          title="Hora del parte"
          template="P2"
          :show-seconds="false"
          :disabled="isUpdatingDate"
          @onchange="handleTimeChange"
        />

        <f7-button
          :preloader="isUpdatingDate"
          :loading="isUpdatingDate"
          :disabled="!shouldConfirmButtonBeDisplayed || isUpdatingDate"
          class="parts-details-change-date-popup__confirm-button"
          fill
          small
          color="green"
          @click="handleConfirmation"
        >
          {{ $t('common.confirm') }}
        </f7-button>
      </f7-block>
    </f7-page>
  </f7-popup>
</template>

<script setup>
// @ts-check

import {
  computed,
  nextTick,
  onMounted,
  ref,
  shallowRef,
  watch,
} from 'vue';
import { useRepairParts } from '@/composables/useRepairParts';
import { HiNotification } from '@/js/common/HINotificationSupport';
import { callSetPartStartDate } from '@/js/services/repairs/ApiRepairs';
import { logError } from '@/js/utils/log';

import TheNavbar from '@/components/TheNavbar.vue';
import UCDatePicker from '@/components/common/UCDatePicker.vue';
import UCTimePicker from '@/components/common/UCTimePicker.vue';

/** @import { Ref, ShallowRef } from 'vue' */

const emit = defineEmits(['success']);

const opened = defineModel('opened', {
  type: Boolean,
  default: false,
});

const isUpdatingDate = ref(false);

/** @type {ShallowRef<InstanceType<typeof UCDatePicker> | null>} */
const datePickerRef = shallowRef(null);

/** @type {ShallowRef<InstanceType<typeof UCTimePicker> | null>} */
const timePickerRef = shallowRef(null);

/** @type {Ref<Date | null>} */
const selectedStartDate = ref(null);

const { workPartDetails } = useRepairParts();

const shouldConfirmButtonBeDisplayed = computed(() => {
  if (!workPartDetails.value || !workPartDetails.value.FechaInicio || !selectedStartDate.value) {
    return false;
  }

  const detailsDate = new Date(workPartDetails.value.FechaInicio);

  return (
    detailsDate.getFullYear() !== selectedStartDate.value.getFullYear()
    || detailsDate.getMonth() !== selectedStartDate.value.getMonth()
    || detailsDate.getDate() !== selectedStartDate.value.getDate()
    || detailsDate.getHours() !== selectedStartDate.value.getHours()
    || detailsDate.getMinutes() !== selectedStartDate.value.getMinutes()
  );
});

async function setInitialValues() {
  if (workPartDetails.value) {
    const date = new Date(workPartDetails.value.FechaInicio);

    // Wait for UCDatePicker and UCTimePicker rendering
    await nextTick();

    setTimeout(() => {
      selectedStartDate.value = date;
      datePickerRef.value?.setDate(new Date('01/10/2024'));
      timePickerRef.value?.SetTime(date);
    }, 100);
  }
}

/**
 * A method that is called when start date is changed.
 * @param {Date | null} value
 */
function handleDateChange(value) {
  if (!selectedStartDate.value) {
    return;
  }

  if (value) {
    const year = value.getFullYear();
    const month = value.getMonth();
    const day = value.getDate();
    const hours = selectedStartDate.value.getHours();
    const minutes = selectedStartDate.value.getMinutes();

    // Create a new Date object with updated values so the computed prop `shouldConfirmButtonBeDisplayed` is updated
    selectedStartDate.value = new Date(year, month, day, hours, minutes);
  }
}

/**
 * A method that is called when start time is changed.
 * @param {string} value
 */
function handleTimeChange(value) {
  if (!selectedStartDate.value) {
    return;
  }

  if (value) {
    const [hours, minutes] = value.split(':').map(Number);

    if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
      // Create a new Date object with updated values so the computed prop shouldConfirmStartDateButtonBeDisplayed is updated

      const year = selectedStartDate.value.getFullYear();
      const month = selectedStartDate.value.getMonth();
      const day = selectedStartDate.value.getDate();

      selectedStartDate.value = new Date(year, month, day, hours, minutes);
    }
  }
}

async function handleConfirmation() {
  if (!workPartDetails.value || !selectedStartDate.value) {
    return;
  }

  try {
    isUpdatingDate.value = true;
    const { data } = await callSetPartStartDate(
      workPartDetails.value.Id_ParteProduccion,
      selectedStartDate.value,
    );
    isUpdatingDate.value = false;

    if (data.Valor === true) {
      opened.value = false;
      emit('success');

      HiNotification.Toast('La hora de inicio del parte se ha cambiado correctamente');
    } else {
      HiNotification.Toast('No se ha podido cambiar la hora de inicio del parte');
    }
  } catch (error) {
    logError(error);
    HiNotification.Toast('No se ha podido cambiar la hora de inicio del parte');
  } finally {
    isUpdatingDate.value = false;
  }
}

watch(opened, () => {
  if (opened.value) {
    setInitialValues();
  }
});

onMounted(async () => {
  setInitialValues();
});
</script>

<style scoped lang="scss">
.parts-details-change-date-popup {
  --f7-popup-tablet-width: 400px;
  --f7-popup-tablet-height: 300px;
}

.parts-details-change-date-popup__confirm-button {
  margin-top: var(--f7-typography-margin);
}
</style>
